import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, Box } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';

import { useCreatePalletModal } from '../model/use-create-pallet-modal';
import type { LocalPallet } from '@/features/pallet-view/model/types';
import type { ConsignmentStatusCode } from '@/entities/consignments';
import { PALLET_TYPES, type PalletType } from '@/entities/consignments/api/palletsApi';

export interface CreatePalletModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (pallets: LocalPallet[]) => void;
  consignmentId: string;
  consignmentStatus?: ConsignmentStatusCode;
  selectedLineIds?: string[];
}

const INVENTORY_UNITS = ['Bulk (kg)', 'Each', 'Litre', 'Pair', 'Part'];

export const CreatePalletModal = ({
  isOpen,
  onClose,
  onSuccess,
  consignmentId,
  consignmentStatus,
  selectedLineIds = []
}: CreatePalletModalProps) => {
  const { t } = useTranslation();

  const {
    selectedLinesInfo,
    isLoading,
    isProcessing,
    error: hookError,
    handleSubmit,
    handleClose
  } = useCreatePalletModal({
    consignmentId,
    consignmentStatus,
    selectedLineIds,
    onSuccess,
    onClose
  });

  // Local form state
  const [selectedType, setSelectedType] = useState<PalletType | null>(null);
  const [inventoryUnitsPerTransport, setInventoryUnitsPerTransport] = useState<string>('');
  const [inventoryUnitType, setInventoryUnitType] = useState<string>('');
  const [transportUnitQuantity, setTransportUnitQuantity] = useState<string>('');
  const [validationError, setValidationError] = useState('');

  const hasSelectedLines = selectedLinesInfo.totalUnits > 0;
  const selectedLinesUnit = selectedLinesInfo.unit;
  const totalSelectedUnits = selectedLinesInfo.totalUnits;

  // Pre-select the unit if all selected lines share the same unit
  useEffect(() => {
    if (selectedLinesUnit && !inventoryUnitType) {
      setInventoryUnitType(selectedLinesUnit);
    }
  }, [selectedLinesUnit, inventoryUnitType]);

  const resetForm = () => {
    setSelectedType(null);
    setInventoryUnitsPerTransport('');
    setInventoryUnitType('');
    setTransportUnitQuantity('');
    setValidationError('');
  };

  const handleModalClose = () => {
    resetForm();
    handleClose();
  };

  const handleTypeSelect = (type: PalletType) => {
    setSelectedType(type);
    setValidationError('');

    // If no lines selected, create empty pallet immediately
    if (!hasSelectedLines) {
      handleSubmit({
        type,
        inventoryUnitsPerTransport: 0,
        inventoryUnitType: '',
        transportUnitQuantity: 1
      });
      resetForm();
    }
  };

  const handleConfirm = () => {
    const invValue = parseInt(inventoryUnitsPerTransport, 10);
    const tuValue = parseInt(transportUnitQuantity, 10);

    if (!tuValue || tuValue <= 0) {
      setValidationError(
        t('pallet.create.error.invalidTransportUnits', 'Please enter a valid transport unit quantity')
      );
      return;
    }
    if (!invValue || invValue <= 0) {
      setValidationError(
        t('pallet.create.error.invalidUnits', 'Please enter a valid number for inventory units per transport')
      );
      return;
    }
    if (!inventoryUnitType) {
      setValidationError(t('pallet.create.error.noUnit', 'Please select an inventory unit'));
      return;
    }

    // Validate: total capacity must be >= total selected units
    const totalCapacity = tuValue * invValue;
    if (totalCapacity < totalSelectedUnits) {
      setValidationError(
        t(
          'pallet.create.error.insufficientCapacity',
          'Not enough capacity. {{pallets}} pallets × {{perPallet}} units = {{total}} total capacity, but selected lines have {{needed}} units. Increase pallets or units per transport.',
          { pallets: tuValue, perPallet: invValue, total: totalCapacity, needed: totalSelectedUnits }
        )
      );
      return;
    }

    handleSubmit({
      type: selectedType!,
      inventoryUnitsPerTransport: invValue,
      inventoryUnitType,
      transportUnitQuantity: tuValue
    });
    resetForm();
  };

  const error = validationError || hookError;

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={handleModalClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex items-center justify-center py-8">
            <Spinner className="h-8 w-8" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleModalClose}>
      <DialogContent
        className="sm:max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('pallet.create.title', 'Create New Pallet')}</DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Mixed units warning */}
          {hasSelectedLines && selectedLinesUnit === null && (
            <Alert variant="destructive">
              <AlertDescription>
                {t(
                  'pallet.create.mixedUnitsWarning',
                  'Selected lines have different inventory units. Please select lines with the same unit.'
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Step 1: Select type */}
          <div>
            <p className="text-sm text-muted-foreground mb-3">
              {t('pallet.create.selectType', 'Select the type of pallet:')}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                className={`flex flex-col items-center gap-3 h-auto py-6 hover:border-primary hover:bg-primary/5 ${
                  selectedType === PALLET_TYPES.WAREHOUSE_PALLET ? 'border-primary bg-primary/5' : ''
                }`}
                onClick={() => handleTypeSelect(PALLET_TYPES.WAREHOUSE_PALLET)}>
                <Package className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm font-medium">{t('pallet.type.warehousePallet', 'Warehouse Pallet')}</span>
              </Button>

              <Button
                variant="outline"
                className={`flex flex-col items-center gap-3 h-auto py-6 hover:border-primary hover:bg-primary/5 ${
                  selectedType === PALLET_TYPES.WOODEN_CRATE ? 'border-primary bg-primary/5' : ''
                }`}
                onClick={() => handleTypeSelect(PALLET_TYPES.WOODEN_CRATE)}>
                <Box className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm font-medium">{t('pallet.type.woodenCrate', 'Wooden Crate')}</span>
              </Button>
            </div>
          </div>

          {/* Step 2: Transport unit quantity + inventory unit (only when lines are selected and same unit) */}
          {hasSelectedLines && selectedType && selectedLinesUnit !== null && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="transportUnitQuantity">
                  {t('pallet.create.transportUnitQuantity', 'Transport unit quantity')}
                </Label>
                <Input
                  id="transportUnitQuantity"
                  type="number"
                  min="1"
                  max="50"
                  value={transportUnitQuantity}
                  onChange={(e) => {
                    setTransportUnitQuantity(e.target.value);
                    setValidationError('');
                  }}
                  placeholder={t('pallet.create.transportUnitPlaceholder', 'e.g. 5')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="inventoryUnitsPerTransport">
                  {t('pallet.create.inventoryUnitsPerTransport', 'Inventory units per transport unit')}
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="inventoryUnitsPerTransport"
                    type="number"
                    min="1"
                    value={inventoryUnitsPerTransport}
                    onChange={(e) => {
                      setInventoryUnitsPerTransport(e.target.value);
                      setValidationError('');
                    }}
                    placeholder={t('pallet.create.inventoryUnitsPlaceholder', 'e.g. 50')}
                    className="flex-1"
                  />
                  <Select
                    value={inventoryUnitType}
                    onValueChange={(v) => {
                      setInventoryUnitType(v);
                      setValidationError('');
                    }}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder={t('pallet.create.selectUnit', 'Unit')} />
                    </SelectTrigger>
                    <SelectContent>
                      {INVENTORY_UNITS.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleConfirm}
                  disabled={
                    !inventoryUnitsPerTransport || !transportUnitQuantity || !inventoryUnitType || isProcessing
                  }>
                  {isProcessing
                    ? t('common.processing', 'Processing...')
                    : t('pallet.create.confirm', 'Create Pallets')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
